import React, { useState, useRef, useEffect } from 'react';

interface DropdownMenuProps {
  children: React.ReactNode;
}

interface DropdownMenuTriggerProps {
  asChild?: boolean;
  children: React.ReactNode;
  toggleMenu: () => void;
}

interface DropdownMenuContentProps {
  isOpen: boolean;
  align?: 'start' | 'end';
  className?: string;
  children: React.ReactNode;
}

interface DropdownMenuItemProps {
  onClick?: () => void;
  children: React.ReactNode;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => setIsOpen((prev) => !prev);

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      {React.Children.map(children, (child) =>
        React.cloneElement(child as React.ReactElement<any>, { isOpen, toggleMenu })
      )}
    </div>
  );
};

export const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({ asChild, children, toggleMenu }) => {
  return (
    <div onClick={toggleMenu} className="cursor-pointer">
      {children}
    </div>
  );
};

export const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({ isOpen, align = 'start', className, children }) => {
  if (!isOpen) return null;

  return (
    <div
      className={`absolute ${align === 'end' ? 'right-0' : 'left-0'} mt-2 w-48 bg-gray-900 text-white shadow-lg rounded-md border border-gray-700 ${className}`}
    >
      {children}
    </div>
  );
};

export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({ onClick, children }) => {
  return (
    <div
      onClick={onClick}
      className="px-4 py-2 hover:bg-gray-800 cursor-pointer"
    >
      {children}
    </div>
  );
};

export default DropdownMenu;
